import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';
import { 
  Filter, Search, Heart, Star, MapPin, Calendar, Users,
  Sliders, TrendingUp, Brain, Eye, Clock, Target,
  Sparkles, Award, Coffee, Music, Camera, Book,
  Dumbbell, Plane, Palette, Code, Mountain, Utensils,
  X, Check, RotateCcw, Bookmark, History, ArrowRight,
  Activity, Zap, MessageCircle, ChevronDown, ChevronUp
} from 'lucide-react';

// Advanced Filter Interface
export const AdvancedFilters = ({ onFiltersChange, initialFilters = {} }) => {
  const [filters, setFilters] = useState({
    ageRange: [22, 35],
    distance: 25,
    interests: [],
    education: '',
    lifestyle: [],
    lookingFor: 'serious',
    activityLevel: 'moderate',
    smoking: 'no',
    drinking: 'socially',
    hasChildren: 'no_preference',
    wantsChildren: 'no_preference',
    height: [150, 190],
    occupation: '',
    ...initialFilters
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  const interestOptions = [
    { id: 'fitness', name: 'Fitness', icon: <Dumbbell className="w-4 h-4" />, color: 'bg-green-100 text-green-700' },
    { id: 'travel', name: 'Travel', icon: <Plane className="w-4 h-4" />, color: 'bg-blue-100 text-blue-700' },
    { id: 'food', name: 'Food & Dining', icon: <Utensils className="w-4 h-4" />, color: 'bg-orange-100 text-orange-700' },
    { id: 'music', name: 'Music', icon: <Music className="w-4 h-4" />, color: 'bg-purple-100 text-purple-700' },
    { id: 'art', name: 'Art & Culture', icon: <Palette className="w-4 h-4" />, color: 'bg-pink-100 text-pink-700' },
    { id: 'tech', name: 'Technology', icon: <Code className="w-4 h-4" />, color: 'bg-indigo-100 text-indigo-700' },
    { id: 'outdoors', name: 'Outdoors', icon: <Mountain className="w-4 h-4" />, color: 'bg-emerald-100 text-emerald-700' },
    { id: 'reading', name: 'Reading', icon: <Book className="w-4 h-4" />, color: 'bg-yellow-100 text-yellow-700' }
  ];

  const lifestyleOptions = [
    { id: 'active', name: 'Active', description: 'Loves sports and fitness' },
    { id: 'social', name: 'Social', description: 'Enjoys parties and events' },
    { id: 'homebody', name: 'Homebody', description: 'Prefers cozy nights in' },
    { id: 'adventurous', name: 'Adventurous', description: 'Always up for new experiences' },
    { id: 'creative', name: 'Creative', description: 'Artistic and expressive' },
    { id: 'intellectual', name: 'Intellectual', description: 'Loves deep conversations' }
  ];

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const toggleInterest = (interestId) => {
    const updated = filters.interests.includes(interestId)
      ? filters.interests.filter(id => id !== interestId)
      : [...filters.interests, interestId];
    updateFilter('interests', updated);
  };

  const toggleLifestyle = (lifestyleId) => {
    const updated = filters.lifestyle.includes(lifestyleId)
      ? filters.lifestyle.filter(id => id !== lifestyleId)
      : [...filters.lifestyle, lifestyleId];
    updateFilter('lifestyle', updated);
  };

  const resetFilters = () => {
    const defaultFilters = {
      ageRange: [22, 35],
      distance: 25,
      interests: [],
      education: '',
      lifestyle: [],
      lookingFor: 'serious',
      activityLevel: 'moderate',
      smoking: 'no',
      drinking: 'socially',
      hasChildren: 'no_preference',
      wantsChildren: 'no_preference',
      height: [150, 190],
      occupation: ''
    };
    setFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.interests.length > 0) count++;
    if (filters.lifestyle.length > 0) count++;
    if (filters.education) count++;
    if (filters.occupation) count++;
    if (filters.lookingFor !== 'serious') count++;
    return count;
  };

  return (
    <Card className="sticky top-4 z-10 bg-white/95 backdrop-blur-md border-rose-200 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-lg font-semibold text-gray-800">
            <Sliders className="w-5 h-5 mr-2 text-rose-500" />
            Smart Filters
            {getActiveFiltersCount() > 0 && (
              <Badge className="ml-2 bg-rose-500 text-white text-xs">
                {getActiveFiltersCount()}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="text-gray-500 hover:text-gray-700"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Basic Filters - Always Visible */}
        <div className="space-y-4">
          {/* Age Range */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Age Range</label>
              <span className="text-sm text-rose-600 font-semibold">
                {filters.ageRange[0]} - {filters.ageRange[1]}
              </span>
            </div>
            <Slider
              value={filters.ageRange}
              onValueChange={(value) => updateFilter('ageRange', value)}
              max={60}
              min={18}
              step={1}
              className="w-full"
            />
          </div>

          {/* Distance */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700 flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                Distance
              </label>
              <span className="text-sm text-rose-600 font-semibold">
                {filters.distance} km
              </span>
            </div>
            <Slider
              value={[filters.distance]}
              onValueChange={(value) => updateFilter('distance', value[0])}
              max={100}
              min={5}
              step={5}
              className="w-full"
            />
          </div>
        </div>

        {/* Expanded Filters */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              {/* Filter Sections */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex space-x-2 mb-4">
                  {[
                    { id: 'interests', name: 'Interests' },
                    { id: 'lifestyle', name: 'Lifestyle' },
                    { id: 'preferences', name: 'Preferences' }
                  ].map(section => (
                    <Button
                      key={section.id}
                      variant={activeSection === section.id ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setActiveSection(section.id)}
                      className={activeSection === section.id ? "bg-rose-100 text-rose-700" : ""}
                    >
                      {section.name}
                    </Button>
                  ))}
                </div>

                {/* Interests Section */}
                {activeSection === 'interests' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Shared Interests
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {interestOptions.map(interest => (
                          <motion.button
                            key={interest.id}
                            onClick={() => toggleInterest(interest.id)}
                            className={`flex items-center space-x-2 p-3 rounded-lg border-2 text-sm transition-all ${
                              filters.interests.includes(interest.id)
                                ? 'border-rose-500 bg-rose-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className={filters.interests.includes(interest.id) ? 'text-rose-500' : 'text-gray-400'}>
                              {interest.icon}
                            </div>
                            <span className={filters.interests.includes(interest.id) ? 'text-rose-700 font-medium' : 'text-gray-700'}>
                              {interest.name}
                            </span>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Lifestyle Section */}
                {activeSection === 'lifestyle' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Lifestyle Compatibility
                      </label>
                      <div className="space-y-2">
                        {lifestyleOptions.map(lifestyle => (
                          <motion.button
                            key={lifestyle.id}
                            onClick={() => toggleLifestyle(lifestyle.id)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                              filters.lifestyle.includes(lifestyle.id)
                                ? 'border-rose-500 bg-rose-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className={`font-medium ${
                              filters.lifestyle.includes(lifestyle.id) ? 'text-rose-700' : 'text-gray-800'
                            }`}>
                              {lifestyle.name}
                            </div>
                            <div className={`text-xs ${
                              filters.lifestyle.includes(lifestyle.id) ? 'text-rose-600' : 'text-gray-500'
                            }`}>
                              {lifestyle.description}
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Preferences Section */}
                {activeSection === 'preferences' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Looking For
                        </label>
                        <select
                          value={filters.lookingFor}
                          onChange={(e) => updateFilter('lookingFor', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-2 focus:ring-rose-200 text-sm"
                        >
                          <option value="casual">Something Casual</option>
                          <option value="serious">Serious Relationship</option>
                          <option value="friends">New Friends</option>
                          <option value="open">Let's see what happens</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Education Level
                        </label>
                        <Input
                          placeholder="e.g., Bachelor's, Master's, PhD"
                          value={filters.education}
                          onChange={(e) => updateFilter('education', e.target.value)}
                          className="text-sm"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Occupation
                        </label>
                        <Input
                          placeholder="e.g., Engineer, Teacher, Artist"
                          value={filters.occupation}
                          onChange={(e) => updateFilter('occupation', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Apply Filters Button */}
        <Button
          className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
          size="lg"
        >
          <Search className="w-4 h-4 mr-2" />
          Find Compatible Matches
        </Button>
      </CardContent>
    </Card>
  );
};

// Compatibility Score Display
export const CompatibilityScore = ({ score, breakdown, isDetailed = false }) => {
  const [showBreakdown, setShowBreakdown] = useState(isDetailed);
  
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-blue-600 bg-blue-100';
    if (score >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 85) return 'Excellent Match';
    if (score >= 70) return 'Great Match';
    if (score >= 50) return 'Good Match';
    return 'Fair Match';
  };

  const getScoreIcon = (score) => {
    if (score >= 85) return <Zap className="w-4 h-4" />;
    if (score >= 70) return <Star className="w-4 h-4" />;
    if (score >= 50) return <Heart className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  return (
    <div className="space-y-3">
      {/* Main Score Display */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-3">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full ${getScoreColor(score)}`}>
            <span className="text-lg font-bold">{score}%</span>
          </div>
          <div>
            <div className="font-semibold text-gray-800 flex items-center">
              {getScoreIcon(score)}
              <span className="ml-1">{getScoreLabel(score)}</span>
            </div>
            <div className="text-sm text-gray-500">
              Compatibility Score
            </div>
          </div>
        </div>
        
        {breakdown && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="text-gray-500 hover:text-gray-700"
          >
            {showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        )}
      </motion.div>

      {/* Score Breakdown */}
      <AnimatePresence>
        {showBreakdown && breakdown && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Compatibility Breakdown</h4>
              
              {Object.entries(breakdown).map(([category, data], index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-rose-400"></div>
                    <span className="text-sm text-gray-700 capitalize">{category}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-rose-400 h-1 rounded-full transition-all"
                        style={{ width: `${data.score}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-8">
                      {data.score}%
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Enhanced Profile Discovery Card
export const EnhancedProfileCard = ({ 
  profile, 
  onLike, 
  onPass, 
  onBookmark, 
  showCompatibility = true,
  showMutualInterests = true 
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showFullBio, setShowFullBio] = useState(false);

  const mutualInterests = profile.mutualInterests || [];
  const compatibilityScore = profile.compatibilityScore || 72;
  const photos = profile.photos || ['/placeholder-avatar.jpg'];
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const nextPhoto = (e) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="relative perspective-1000"
    >
      <motion.div
        className={`relative w-full h-[600px] transform-style-preserve-3d transition-transform duration-500 ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
      >
        {/* Front of Card */}
        <Card className="absolute inset-0 backface-hidden bg-white shadow-xl border-0 overflow-hidden">
          {/* Photo Section */}
          <div className="relative h-80 bg-gray-200">
            <img
              src={photos[currentPhotoIndex]}
              alt={`${profile.name} photo ${currentPhotoIndex + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400';
              }}
            />
            
            {/* Photo Navigation */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white rounded-full p-2 transition-all"
                >
                  <ChevronUp className="w-4 h-4 rotate-[-90deg]" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white rounded-full p-2 transition-all"
                >
                  <ChevronUp className="w-4 h-4 rotate-90" />
                </button>
                
                {/* Photo Dots */}
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1">
                  {photos.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentPhotoIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Bookmark Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBookmark(profile.id);
              }}
              className="absolute top-3 right-3 bg-black/20 hover:bg-black/40 text-white rounded-full p-2 transition-all"
            >
              <Bookmark className="w-4 h-4" />
            </button>

            {/* Compatibility Badge */}
            {showCompatibility && (
              <div className="absolute top-3 left-3">
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  compatibilityScore >= 80 ? 'bg-green-500 text-white' :
                  compatibilityScore >= 60 ? 'bg-blue-500 text-white' :
                  'bg-yellow-500 text-black'
                }`}>
                  {compatibilityScore}% Match
                </div>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <CardContent className="p-4 h-[320px] overflow-hidden">
            <div className="space-y-3">
              {/* Name and Age */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {profile.name}, {profile.age}
                  </h3>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <MapPin className="w-3 h-3 mr-1" />
                    {profile.location} • {profile.distance}km away
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFlipped(true)}
                  className="text-rose-500 hover:text-rose-600"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>

              {/* Bio Preview */}
              <div className="space-y-2">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {showFullBio ? profile.bio : `${profile.bio.substring(0, 120)}...`}
                </p>
                {profile.bio.length > 120 && (
                  <button
                    onClick={() => setShowFullBio(!showFullBio)}
                    className="text-xs text-rose-500 hover:text-rose-600 font-medium"
                  >
                    {showFullBio ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>

              {/* Mutual Interests */}
              {showMutualInterests && mutualInterests.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-green-600 mb-2 flex items-center">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {mutualInterests.length} Shared Interest{mutualInterests.length > 1 ? 's' : ''}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {mutualInterests.slice(0, 4).map((interest, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="text-xs bg-green-50 text-green-700 border-green-200"
                      >
                        {interest}
                      </Badge>
                    ))}
                    {mutualInterests.length > 4 && (
                      <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600">
                        +{mutualInterests.length - 4} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Occupation */}
              <div className="flex items-center text-sm text-gray-600">
                <Users className="w-4 h-4 mr-2" />
                {profile.occupation}
              </div>

              {/* Last Active */}
              <div className="flex items-center text-xs text-gray-500">
                <Activity className="w-3 h-3 mr-1" />
                Active {profile.lastActive}
              </div>
            </div>
          </CardContent>

          {/* Action Buttons */}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex justify-center space-x-4">
              <motion.button
                onClick={() => onPass(profile.id)}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                onClick={() => onLike(profile.id)}
                className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 transition-colors shadow-lg"
                whileTap={{ scale: 0.9 }}
              >
                <Heart className="w-6 h-6" />
              </motion.button>
              
              <motion.button
                onClick={() => {/* Super Like */}}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                whileTap={{ scale: 0.9 }}
              >
                <Star className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </Card>

        {/* Back of Card - Detailed Info */}
        <Card className="absolute inset-0 backface-hidden rotate-y-180 bg-white shadow-xl border-0 overflow-hidden">
          <CardContent className="p-6 h-full overflow-y-auto">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">
                  More about {profile.name}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFlipped(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                </Button>
              </div>

              {/* Compatibility Score Detailed */}
              {showCompatibility && (
                <CompatibilityScore
                  score={compatibilityScore}
                  breakdown={{
                    interests: { score: 85 },
                    lifestyle: { score: 72 },
                    values: { score: 68 },
                    communication: { score: 76 },
                    goals: { score: 71 }
                  }}
                  isDetailed={true}
                />
              )}

              {/* Interests */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Interests & Hobbies</h4>
                <div className="flex flex-wrap gap-2">
                  {(profile.interests || []).map((interest, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className={`${
                        mutualInterests.includes(interest)
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      {mutualInterests.includes(interest) && (
                        <Check className="w-3 h-3 mr-1" />
                      )}
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Looking For */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Looking For</h4>
                <p className="text-sm text-gray-600">{profile.lookingFor}</p>
              </div>

              {/* Education & Work */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Background</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>🎓 {profile.education}</div>
                  <div>💼 {profile.occupation}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

// Smart Browse History
export const SmartBrowseHistory = ({ viewedProfiles, onRevisit, onClearHistory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="bg-white/95 backdrop-blur-md border-gray-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center">
            <History className="w-4 h-4 mr-2" />
            Recently Viewed ({viewedProfiles.length})
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearHistory}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <CardContent className="pt-0">
              <div className="grid grid-cols-4 gap-2">
                {viewedProfiles.slice(0, 8).map((profile, index) => (
                  <motion.button
                    key={profile.id}
                    onClick={() => onRevisit(profile.id)}
                    className="relative group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img
                      src={profile.photo}
                      alt={profile.name}
                      className="w-full h-16 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 rounded-lg transition-all" />
                    <div className="absolute bottom-1 left-1 right-1">
                      <div className="text-xs text-white font-medium truncate">
                        {profile.name}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
              
              {viewedProfiles.length > 8 && (
                <div className="text-center mt-3">
                  <Button variant="ghost" size="sm" className="text-xs text-gray-500">
                    View All ({viewedProfiles.length})
                  </Button>
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

export default {
  AdvancedFilters,
  CompatibilityScore,
  EnhancedProfileCard,
  SmartBrowseHistory
};